import apiClient from "./apiClient";


const getDurationForQuestionCount = (questionCount) => {
  
  const totalMinutes = questionCount * 2;

  if (totalMinutes < 60) {
    return `${totalMinutes} minutes`;
  } else {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${hours} hours ${minutes} minutes` : `${hours} hours`;
  }
};

const getTestType = (testName) => {
  const lowerName = (testName || "").toLowerCase();

  if (lowerName.includes("practice")) {
    return "Practice";
  } else if (lowerName.includes("quiz")) {
    return "Quiz";
  } else if (lowerName.includes("full") || lowerName.includes("complete")) {
    return "Full Test";
  } else {
    return "Test";
  }
};

const testService = {

  getAvailableTests: async () => {
    try {
      
      const response = await apiClient.get("/studenttests/available");
      if (!response || !response.data) {
        throw new Error("No test data received from server");
      }

      
      const formattedTests = response.data.map((test) => ({
        id: test.id,
        name: test.name,
        description: test.description || "",
        type: getTestType(test.name), 
        questions: test.questionsCount || 0,
        duration: getDurationForQuestionCount(test.questionsCount || 0),
        isVisible: test.isVisible,
        available: true,
      }));

      return formattedTests;
    } catch (error) {
      
      return [];
    }
  },

  getMyFeedback: async (testId) => {
    try {
      const response = await apiClient.get(`/studenttests/${testId}/my-feedback`);
      return response.data;
    } catch (error) {
      return { notViewedByTeacherYet: true };
    }
  },

  getTestById: async (testId) => {
    try {
      
      const response = await apiClient.get(`/studenttests/${testId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  startTest: async (testId) => {
    try {
      
      const response = await apiClient.get(`/studenttests/${testId}/start`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  submitAnswer: async (testSessionId, questionId, answer) => {
    try {
      const response = await apiClient.post(
        `/studenttests/${testSessionId}/answer`,
        {
          questionId,
          answer,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  completeTest: async (testSessionId, stats, answers) => {
    try {
      const studentPkStr = localStorage.getItem('student_pk');
      const studentId = studentPkStr ? parseInt(studentPkStr, 10) : null;
      const payload = {
        studentId: studentId,
        correctCount: stats.correctCount,
        incorrectCount: stats.incorrectCount,
        score: stats.score,
        answers 
      };
      const response = await apiClient.post(
        `/studenttests/${testSessionId}/complete`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  // NOTE: Admin-only endpoint; student frontend should not call this directly.
  getTestResults: async (testSessionId) => {
    try {
      const response = await apiClient.get(
        `/studenttests/${testSessionId}/results`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  getMyTestResults: async (testId) => {
    try {
      const response = await apiClient.get(`/studenttests/${testId}/my-results`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },


  getMyResults: async () => {
    try {
      const studentPk = localStorage.getItem('student_pk') ?? localStorage.getItem('student_id');
      const response = await apiClient.get(`/studenttests/student/${studentPk}`);
      const data = response.data;
      
      let questions = data.questions ?? data.Questions ?? [];
      let results = data.results ?? data.Results ?? [];
      
      if (Array.isArray(data)) {
        results = data;
        
        const qmap = new Map();
        data.forEach(r => r.details.forEach(d => qmap.set(d.questionNumber ?? d.QuestionNumber, d.questionType ?? d.QuestionType)));
        questions = Array.from(qmap.entries()).map(([number, type]) => ({ number, type }));
      }
      return { questions, results };
    } catch (error) {
      return { questions: [], results: [] };
    }
  },

  getMyPercentiles: async () => {
    try {
      const studentPk = localStorage.getItem('student_pk') ?? localStorage.getItem('student_id');
      const response = await apiClient.get(`/studenttests/student/${studentPk}/percentiles`);
      return response.data;
    } catch (error) {
      return { Results: [] };
    }
  },

  getLeaderboard: async (testId, full = false) => {
    try {
      const url = full ? `/studenttests/${testId}/leaderboard?full=true` : `/studenttests/${testId}/leaderboard`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      return { TestId: testId, TestName: '', Total: 0, Top: [], Me: null };
    }
  },

  /**
   * Fetch detailed question list for a given test (including options and question numbers).
   */
  getQuestionsByTest: async (testId) => {
    try {
      const response = await apiClient.get(`/studenttests/${testId}/questions`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Notifications (server-backed)
  getNotifications: async () => {
    try {
      const response = await apiClient.get('/studenttests/notifications');
      return response.data; // { Items: [...] }
    } catch (error) {
      return { Items: [] };
    }
  },

  markNotificationRead: async (id) => {
    try {
      const response = await apiClient.put(`/studenttests/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      return { id, viewedAt: null };
    }
  },

  dismissNotification: async (id) => {
    try {
      const response = await apiClient.delete(`/studenttests/notifications/${id}`);
      return response.data;
    } catch (error) {
      return { id, dismissedAt: null };
    }
  },
};

export default testService;
